import { memo } from 'react';
import { motion } from 'framer-motion';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { useReduceMotion } from '@/hooks/useReduceMotion';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

/**
 * Background Layers Component
 * Renders the layered futuristic background for UI V2
 * - Base gradient
 * - Aurora blobs (animated)
 * - Grid overlay
 * 
 * Automatically disables in LOW_FX mode
 */
export const BackgroundLayers = memo(function BackgroundLayers() {
  const { uiV2Enabled, isLowFx, theme, partyMode } = useUIV2Safe();
  const reduceMotion = useReduceMotion();
  const isMobile = useIsMobile();
  
  if (!uiV2Enabled) return null;
  
  // On mobile, skip all animated blobs and grid — just render the static base gradient
  const shouldAnimate = !isLowFx && !reduceMotion && !isMobile;
  
  return (
    <div 
      className={cn(
        'v2-background-layers fixed inset-0 pointer-events-none -z-10 overflow-hidden',
        'v2-only'
      )}
      aria-hidden="true"
    >
      {/* Base gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% -20%, hsl(var(--primary) / 0.1) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 0%, hsl(var(--accent) / 0.08) 0%, transparent 40%),
            radial-gradient(ellipse 50% 30% at 0% 100%, hsl(var(--ring) / 0.06) 0%, transparent 40%),
            hsl(var(--background))
          `,
        }}
      />
      
      {/* Aurora Layer 1 - Primary color blob */}
      {shouldAnimate && (
        <motion.div
          className="v2-aurora absolute w-[150%] h-[150%] -top-1/4 -left-1/4"
          style={{
            background: `
              radial-gradient(ellipse 60% 40% at 30% 30%, 
                hsl(var(--primary) / var(--aurora-opacity)) 0%, 
                transparent 50%
              )
            `,
            filter: 'blur(40px)',
          }}
          animate={partyMode ? {
            x: ['0%', '15%', '-10%', '0%'],
            y: ['0%', '-15%', '10%', '0%'],
            scale: [1, 1.2, 0.9, 1],
          } : {
            x: ['0%', '8%', '-5%', '0%'],
            y: ['0%', '-6%', '4%', '0%'],
            scale: [1, 1.04, 0.98, 1],
          }}
          transition={{
            duration: partyMode ? 5 : 25,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
      
      {/* Aurora Layer 2 - Accent color blob */}
      {shouldAnimate && (
        <motion.div
          className="v2-aurora absolute w-[150%] h-[150%] -top-1/4 -left-1/4"
          style={{
            background: `
              radial-gradient(ellipse 50% 50% at 70% 60%, 
                hsl(var(--accent) / calc(var(--aurora-opacity) * 0.7)) 0%, 
                transparent 45%
              )
            `,
            filter: 'blur(50px)',
          }}
          animate={partyMode ? {
            x: ['0%', '-20%', '15%', '0%'],
            y: ['0%', '12%', '-8%', '0%'],
            scale: [1, 0.85, 1.15, 1],
          } : {
            x: ['0%', '-10%', '6%', '0%'],
            y: ['0%', '8%', '-4%', '0%'],
            scale: [1, 0.96, 1.05, 1],
          }}
          transition={{
            duration: partyMode ? 4 : 30,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      )}
      
      {/* Aurora Layer 3 - Ring color blob */}
      {shouldAnimate && (
        <motion.div
          className="v2-aurora absolute w-[120%] h-[120%] -top-1/4 -left-1/4"
          style={{
            background: `
              radial-gradient(ellipse 40% 35% at 50% 80%, 
                hsl(var(--ring) / calc(var(--aurora-opacity) * 0.5)) 0%, 
                transparent 40%
              )
            `,
            filter: 'blur(60px)',
          }}
          animate={partyMode ? {
            x: ['0%', '10%', '-15%', '0%'],
            y: ['0%', '-18%', '10%', '0%'],
            rotate: [0, 15, -10, 0],
          } : {
            x: ['0%', '4%', '-6%', '0%'],
            y: ['0%', '-8%', '5%', '0%'],
            rotate: [0, 3, -2, 0],
          }}
          transition={{
            duration: partyMode ? 3 : 35,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 4,
          }}
        />
      )}
      
      {/* Grid Overlay */}
      {!isLowFx && !isMobile && (
        <div 
          className="v2-grid-overlay absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(var(--grid-color) 1px, transparent 1px),
              linear-gradient(90deg, var(--grid-color) 1px, transparent 1px)
            `,
            backgroundSize: 'var(--grid-size) var(--grid-size)',
            maskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 0%, transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 0%, transparent 70%)',
          }}
        />
      )}
      
      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.015] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      
      {/* Vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, hsl(var(--background) / 0.4) 70%, hsl(var(--background) / 0.7) 100%)',
        }}
      />
    </div>
  );
});
